import SelectComponent from "@via/components/select/select.tsx";
import { useAuth } from "@via/hooks/useAuth";
import { useIntegrationTokens } from "@via/api/integration-tokens.api";
import type { SelectOption } from "@via/utils/interfaces.tsx";
import { useMemo, type FC } from "react";
import { type Control } from "react-hook-form";
import type { Options } from "react-select";

interface IntegrationTokenSelectorProps {
  control: Control<any>;
  name: string;
  /** Array of token types to filter (e.g., ["openai", "tamarind"]) */
  tokenTypes: string[];
  readOnly?: boolean;
  /** Called when selection changes (token id string or null). Use to sync with paramsInputs. */
  onSelectionChange?: (tokenId: string | null) => void;
  /** When set, the "no tokens" message is shown only to the run owner. */
  runOwnerId?: number;
}

/**
 * A dropdown component that fetches and displays integration tokens
 * based on the specified token types.
 */
const IntegrationTokenSelector: FC<IntegrationTokenSelectorProps> = ({
  control,
  name,
  tokenTypes,
  readOnly = false,
  onSelectionChange,
  runOwnerId,
}) => {
  const { user } = useAuth();
  const isRunOwner = runOwnerId != null ? user?.id === runOwnerId : false;
  // Build the filter for token types - only apply filter if tokenTypes is non-empty
  const filters = useMemo(
    () =>
      tokenTypes && tokenTypes.length > 0
        ? [{ id: "tokenTypeIn", value: tokenTypes }]
        : [],
    [tokenTypes]
  );

  // Fetch integration tokens with the specified types (or all if no filter)
  const { data: tokensData, isLoading } = useIntegrationTokens(
    0,
    100,
    [],
    filters,
    false
  );

  // Map tokens to select options
  const tokenOptions = useMemo(
    () =>
      tokensData?.data?.map(token => ({
        label: `${token.name} (${token.tokenType})`,
        value: token.id.toString(),
      })) || [],
    [tokensData?.data]
  );

  const handleChange = (selected: SelectOption | Options<SelectOption>) => {
    const single = Array.isArray(selected) ? selected[0] : selected;
    const val = single?.value;
    onSelectionChange?.(val != null ? String(val) : null);
  };

  return (
    <div className="w-full">
      <SelectComponent
        control={control}
        name={name}
        options={tokenOptions}
        placeholder={
          isLoading ? "Loading tokens..." : "Select an integration token"
        }
        isLoading={isLoading}
        readOnly={readOnly}
        enableClear={true}
        onChange={onSelectionChange ? handleChange : undefined}
      />
      {!isLoading && tokenOptions.length === 0 && isRunOwner && (
        <p className="mt-2 text-xs text-amber-600">
          {tokenTypes && tokenTypes.length > 0
            ? `No integration tokens found for types: ${JSON.stringify(tokenTypes)}. Please create one in your profile settings under Integration Tokens.`
            : "No integration tokens found. Please create one in your profile settings under Integration Tokens."}
        </p>
      )}
    </div>
  );
};

export default IntegrationTokenSelector;
